import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Fade,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import CloseIcon from "@mui/icons-material/Close";

interface Props {
  open: boolean;
  sectionRejectionLabels: string[];
  narrativeCommentCount: number;
  generalCommentCount: number;
  /**
   * Count of existing top-level unresolved comments from the current cycle.
   * When > 0 the CO can confirm re-rejection even with no newly-staged
   * comments — typical post-retract flow where the same feedback gets sent
   * back to the officer without typing anything new.
   */
  existingUnresolvedCount?: number;
  onClose: () => void;
  onConfirm: () => void;
  loading?: boolean;
}

/**
 * Final confirmation modal shown before the CO's Reject submission goes through. Replaces
 * the older drawer that mixed editing + submission in one surface. All editing now happens
 * on the main CO Review page; this dialog only summarizes what's staged and confirms.
 */
export function RejectConfirmationDialog({
  open,
  sectionRejectionLabels,
  narrativeCommentCount,
  generalCommentCount,
  existingUnresolvedCount = 0,
  onClose,
  onConfirm,
  loading,
}: Props) {
  const theme = useTheme();
  const stagedTotal =
    sectionRejectionLabels.length + narrativeCommentCount + generalCommentCount;
  const total = stagedTotal + existingUnresolvedCount;

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      aria-labelledby="reject-dialog-title"
      aria-describedby="reject-dialog-description"
      TransitionComponent={Fade}
      transitionDuration={200}
      PaperProps={{
        elevation: 0,
        sx: {
          borderRadius: 3,
          minWidth: 380,
          maxWidth: 480,
          overflow: "visible",
          border: "1px solid",
          borderColor: "divider",
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.6)",
            backdropFilter: "blur(4px)",
          },
        },
      }}
    >
      <IconButton
        onClick={onClose}
        disabled={loading}
        size="small"
        sx={{
          position: "absolute",
          right: 12,
          top: 12,
          color: "text.secondary",
          "&:hover": { backgroundColor: "action.hover" },
        }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>

      <DialogContent sx={{ pt: 4, pb: 2, px: 4, textAlign: "center" }}>
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: "50%",
            backgroundColor: "warning.main",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 20px",
            boxShadow: `0 4px 14px ${alpha(theme.palette.warning.main, 0.3)}`,
          }}
        >
          <ReportProblemOutlinedIcon sx={{ fontSize: 32, color: "white" }} />
        </Box>

        <Typography
          id="reject-dialog-title"
          variant="h5"
          component="h2"
          sx={{ fontWeight: 600, mb: 1.5, color: "text.primary" }}
        >
          Reject report?
        </Typography>

        <Typography
          id="reject-dialog-description"
          variant="body1"
          sx={{ color: "text.secondary", lineHeight: 1.6 }}
        >
          The report will be sent back to the officer with your feedback.
          They&apos;ll revise and resubmit for your review.
        </Typography>

        <Box
          sx={{
            mt: 3,
            p: 1.75,
            borderRadius: 2,
            border: `1px solid ${theme.palette.warning.main}`,
            bgcolor: alpha(theme.palette.warning.main, 0.06),
            textAlign: "left",
          }}
        >
          <Typography variant="caption" color="text.secondary" display="block">
            Sending {total} comment{total === 1 ? "" : "s"}
          </Typography>
          <List dense disablePadding>
            {sectionRejectionLabels.length > 0 && (
              <ListItem disableGutters sx={{ py: 0.25 }}>
                <ListItemText
                  primary={`${sectionRejectionLabels.length} section rejection${sectionRejectionLabels.length === 1 ? "" : "s"}`}
                  secondary={sectionRejectionLabels.join(", ")}
                  primaryTypographyProps={{ variant: "body2", fontWeight: 600 }}
                  secondaryTypographyProps={{ variant: "caption" }}
                />
              </ListItem>
            )}
            {narrativeCommentCount > 0 && (
              <ListItem disableGutters sx={{ py: 0.25 }}>
                <ListItemText
                  primary={`${narrativeCommentCount} narrative inline comment${narrativeCommentCount === 1 ? "" : "s"}`}
                  primaryTypographyProps={{ variant: "body2", fontWeight: 600 }}
                />
              </ListItem>
            )}
            {generalCommentCount > 0 && (
              <ListItem disableGutters sx={{ py: 0.25 }}>
                <ListItemText
                  primary={`${generalCommentCount} general comment${generalCommentCount === 1 ? "" : "s"}`}
                  primaryTypographyProps={{ variant: "body2", fontWeight: 600 }}
                />
              </ListItem>
            )}
            {existingUnresolvedCount > 0 && (
              <ListItem disableGutters sx={{ py: 0.25 }}>
                <ListItemText
                  primary={`${existingUnresolvedCount} existing unresolved comment${existingUnresolvedCount === 1 ? "" : "s"}`}
                  secondary="Previously written; officer will see them again."
                  primaryTypographyProps={{ variant: "body2", fontWeight: 600 }}
                  secondaryTypographyProps={{ variant: "caption" }}
                />
              </ListItem>
            )}
          </List>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 4,
          pb: 4,
          pt: 2,
          gap: 1.5,
          flexDirection: "column",
          alignItems: "stretch",
          "& > :not(:first-of-type)": { marginLeft: 0 },
        }}
      >
        <Button
          onClick={onConfirm}
          color="error"
          variant="contained"
          startIcon={<ReportProblemOutlinedIcon />}
          loading={loading}
          disabled={total === 0}
          fullWidth
          disableElevation
          autoFocus
          sx={{
            py: 1.25,
            borderRadius: 2,
            textTransform: "none",
            fontSize: "0.95rem",
            fontWeight: 600,
            "&:hover": { backgroundColor: "error.dark" },
          }}
        >
          Reject &amp; send back
        </Button>
        <Button
          onClick={onClose}
          disabled={loading}
          color="inherit"
          variant="text"
          fullWidth
          sx={{
            py: 1.25,
            borderRadius: 2,
            textTransform: "none",
            fontSize: "0.95rem",
            fontWeight: 500,
            color: "text.secondary",
            "&:hover": { backgroundColor: "action.hover" },
          }}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}
